import * as Yup from "yup";
import {
  ADD_DELIVERABLE_TYPE,
  AddDeliverableType,
  EXTENDED_SPECIAL_CHARS,
  INPUT_LIMIT,
} from "../../../helpers/constants";
import { UnitTypesKey } from "../../../helpers/constants/siteCoreKeys";
import { KeyValueMap } from "../../../redux/slices/appState/models";
import {
  CreateActivityDefaultFormKey,
  CreateActivityGeneralFormKey,
  CreateDeliverablesFormKey,
  ManageLocales,
  ProgramTemplateAddCompatibleFormKey,
  ProgramTemplateFormKey,
  UpdateDeliverablesFormKey,
} from "./Models";
import { specialCharactersNotAllowedValidation } from "../../../helpers/constants/commonValidationSchemaUtils";
import { createProgramSiteCoreKeys } from "../siteCore/SiteCoreKeys";

export const CreateProgramTemplateSchema = (
  commonValidationMessages: KeyValueMap,
  mandatoryNameByLanguage: string,
  isCompatibleValidate: boolean,
) =>
  Yup.object().shape({
    [ProgramTemplateFormKey.programName]: specialCharactersNotAllowedValidation(
      commonValidationMessages.requiredValidation,
      commonValidationMessages.fieldContainsInvalidCharactersErrorMessage,
    ),
    [ProgramTemplateFormKey.lineOfBusiness]: Yup.string().required(
      commonValidationMessages.requiredValidation,
    ),
    [ProgramTemplateFormKey.programFrameWork]: Yup.string().required(
      commonValidationMessages.requiredValidation,
    ),
    [ProgramTemplateFormKey.solutionCategory]: Yup.string().required(
      commonValidationMessages.requiredValidation,
    ),
    [ProgramTemplateFormKey.solutionCategoryLob]: Yup.string().required(
      commonValidationMessages.requiredValidation,
    ),
    [ProgramTemplateFormKey.solutionOffering]: Yup.string().required(
      commonValidationMessages.requiredValidation,
    ),
    [ProgramTemplateFormKey.programClass]: Yup.string().required(
      commonValidationMessages.requiredValidation,
    ),
    [ProgramTemplateFormKey.closingReasonType]: Yup.string().required(
      commonValidationMessages.requiredValidation,
    ),
    [ProgramTemplateFormKey.duration]: Yup.number()
      .required(commonValidationMessages.durationRequiredErrorMessage)
      .typeError(commonValidationMessages.durationRequiredErrorMessage)
      .min(1, commonValidationMessages.valueRangeFieldValidator1To99)
      .max(99, commonValidationMessages.valueRangeFieldValidator1To99),
    [ProgramTemplateFormKey.status]: Yup.string().default(""),
    [ProgramTemplateFormKey.programNameByLanguage]: mandatoryNameByLanguage
      ? Yup.string()
          .required(commonValidationMessages.requiredValidation)
          .matches(
            INPUT_LIMIT.ALPHA_NUMERIC_WITH_SOME_SPECIAL_CHAR,
            commonValidationMessages.onlyAlphanumericSpacesAllowed,
          )
      : Yup.string().default(""),
    [ProgramTemplateFormKey.nextAccess]: Yup.string().required(
      commonValidationMessages.requiredValidation,
    ),
    [ProgramTemplateFormKey.selectRegionType]: isCompatibleValidate
      ? Yup.string().required(commonValidationMessages.requiredValidation)
      : Yup.string().default(""),
    [ProgramTemplateFormKey.country]: Yup.array()
      .of(Yup.string().required())
      .default([])
      .when(ProgramTemplateFormKey.selectRegionType, {
        is: (selectRegionType: string) =>
          selectRegionType === createProgramSiteCoreKeys.multipleCountries,
        then: (schema) =>
          schema.min(1, commonValidationMessages.requiredValidation),
        otherwise: (schema) => schema,
      }),
  });

export const manageLocaleValidationSchema = (
  mergedLanguages: ManageLocales[],
  commonValidationMessages: KeyValueMap,
) =>
  Yup.object().shape(
    mergedLanguages.reduce(
      (acc, { key, isMandatory, isEnglish }) => {
        acc[key] =
          isMandatory || isEnglish
            ? Yup.string()
                .required(commonValidationMessages.requiredValidation)
                .test(
                  EXTENDED_SPECIAL_CHARS,
                  commonValidationMessages.fieldContainsInvalidCharactersErrorMessage,
                  (value) =>
                    !value ||
                    INPUT_LIMIT.ALPHA_NUMERIC_WITH_EXTENDED_SPECIAL_CHARS.test(
                      value,
                    ),
                )
            : Yup.string()
                .optional()
                .test(
                  EXTENDED_SPECIAL_CHARS,
                  commonValidationMessages.fieldContainsInvalidCharactersErrorMessage,
                  (value) =>
                    !value ||
                    INPUT_LIMIT.ALPHA_NUMERIC_WITH_EXTENDED_SPECIAL_CHARS.test(
                      value,
                    ),
                );
        return acc;
      },
      {} as Record<string, Yup.StringSchema>,
    ),
  );

export const CreateActivityGeneralValidationSchema = (
  commonValidationMessages: KeyValueMap,
  userLang: string,
) =>
  Yup.object().shape({
    [CreateActivityGeneralFormKey.candidateStatus]: Yup.array()
      .min(1, commonValidationMessages.requiredValidation)
      .default([]),
    [CreateActivityGeneralFormKey.activityType]: Yup.string().required(
      commonValidationMessages.requiredValidation,
    ),
    [CreateActivityGeneralFormKey.modality]: Yup.array()
      .min(1, commonValidationMessages.requiredValidation)
      .default([]),
    [CreateActivityGeneralFormKey.topic]: Yup.object().shape({
      [userLang]: specialCharactersNotAllowedValidation(
        commonValidationMessages.requiredValidation,
        commonValidationMessages.fieldContainsInvalidCharactersErrorMessage,
      ),
    }),
    [CreateActivityGeneralFormKey.deliverables]: Yup.array().default([]),
    [CreateActivityGeneralFormKey.activityStatus]: Yup.string().required(
      commonValidationMessages.requiredValidation,
    ),
    [CreateActivityGeneralFormKey.triggerBilling]: Yup.boolean().default(false),
    [CreateActivityGeneralFormKey.lastContactDate]:
      Yup.boolean().default(false),
    [CreateActivityGeneralFormKey.triggerTouch]: Yup.boolean().default(false),
  });

export const CreateActivityDefaultValidationSchema = (
  commonValidationMessages: KeyValueMap,
) =>
  Yup.object().shape(
    {
      [CreateActivityDefaultFormKey.units]: Yup.string().required(
        commonValidationMessages.requiredValidation,
      ),
      [CreateActivityDefaultFormKey.measurementMinute]: Yup.string()
        .when([CreateActivityDefaultFormKey.units], {
          is: (unitValue: string) => {
            return unitValue === UnitTypesKey.Minutes;
          },
          then: (schema) =>
            schema.required(commonValidationMessages.requiredValidation),
        })
        .default(""),
      [CreateActivityDefaultFormKey.measurementOther]: Yup.number()
        .nullable()
        .when([CreateActivityDefaultFormKey.units], {
          is: (unitValue: string) => {
            return (
              unitValue === UnitTypesKey.Count ||
              unitValue === UnitTypesKey.Days
            );
          },
          then: (schema) =>
            schema
              .required(commonValidationMessages.requiredValidation)
              .typeError(commonValidationMessages.requiredValidation)
              .min(1, commonValidationMessages.valueRangeFieldValidator1To99)
              .max(99, commonValidationMessages.valueRangeFieldValidator1To99),
        })
        .default(0),
      // country field is optional, no required validation here
      [CreateActivityDefaultFormKey.country]: Yup.string(),
      [CreateActivityDefaultFormKey.countryMeasurementMinute]: Yup.string()
        .when(
          [
            CreateActivityDefaultFormKey.units,
            CreateActivityDefaultFormKey.country,
          ],
          {
            is: (unitValue: string, country: string) => {
              return unitValue === UnitTypesKey.Minutes && country;
            },
            then: (schema) =>
              schema.required(commonValidationMessages.requiredValidation),
          },
        )
        .default(""),
      [CreateActivityDefaultFormKey.countryMeasurementOther]: Yup.number()
        .when(
          [
            CreateActivityDefaultFormKey.units,
            CreateActivityDefaultFormKey.country,
          ],
          {
            is: (unitValue: string, country: string) => {
              return (
                (unitValue === UnitTypesKey.Count ||
                  unitValue === UnitTypesKey.Days) &&
                country
              );
            },
            then: (schema) =>
              schema
                .required(commonValidationMessages.requiredValidation)
                .typeError(commonValidationMessages.requiredValidation)
                .min(1, commonValidationMessages.valueRangeFieldValidator1To99)
                .max(
                  99,
                  commonValidationMessages.valueRangeFieldValidator1To99,
                ),
          },
        )
        .default(0),
    },
    [
      [
        CreateActivityDefaultFormKey.countryMeasurementMinute,
        CreateActivityDefaultFormKey.countryMeasurementMinute,
      ],
      [
        CreateActivityDefaultFormKey.countryMeasurementOther,
        CreateActivityDefaultFormKey.countryMeasurementOther,
      ],
    ],
  );

export const AddDeliverableValidationSchema = (
  commonValidationMessages: KeyValueMap,
) =>
  Yup.object().shape({
    [CreateDeliverablesFormKey.addDeliverableType]:
      Yup.mixed<AddDeliverableType>()
        .oneOf([
          ADD_DELIVERABLE_TYPE.newKey as AddDeliverableType,
          ADD_DELIVERABLE_TYPE.existingKey as AddDeliverableType,
        ])
        .required()
        .default(ADD_DELIVERABLE_TYPE.newKey as AddDeliverableType),

    [CreateDeliverablesFormKey.deliverableType]: Yup.string().when(
      [CreateDeliverablesFormKey.addDeliverableType],
      {
        is: (addDeliverableType: AddDeliverableType) =>
          addDeliverableType === ADD_DELIVERABLE_TYPE.newKey,
        then: (schema) =>
          schema.required(commonValidationMessages.requiredValidation),
        otherwise: (schema) => schema.notRequired(),
      },
    ),

    [CreateDeliverablesFormKey.name]: specialCharactersNotAllowedValidation(
      commonValidationMessages.requiredValidation,
      commonValidationMessages.fieldContainsInvalidCharactersErrorMessage,
    )
      .default("")
      .when([CreateDeliverablesFormKey.addDeliverableType], {
        is: (addDeliverableType: AddDeliverableType) =>
          addDeliverableType === ADD_DELIVERABLE_TYPE.newKey,
        then: (schema) =>
          schema.required(commonValidationMessages.requiredValidation),
        otherwise: (schema) => schema.notRequired(),
      }),

    [CreateDeliverablesFormKey.units]: Yup.string()
      .required(commonValidationMessages.requiredValidation)
      .when([CreateDeliverablesFormKey.addDeliverableType], {
        is: (addDeliverableType: AddDeliverableType) =>
          addDeliverableType === ADD_DELIVERABLE_TYPE.newKey,
        then: (schema) =>
          schema.required(commonValidationMessages.requiredValidation),
        otherwise: (schema) => schema.notRequired(),
      }),

    [CreateDeliverablesFormKey.measurementMinute]: Yup.string()
      .nullable()
      .when(
        [
          CreateDeliverablesFormKey.units,
          CreateDeliverablesFormKey.addDeliverableType,
        ],
        {
          is: (unitValue: string, addDeliverableType: AddDeliverableType) =>
            addDeliverableType === ADD_DELIVERABLE_TYPE.newKey &&
            unitValue === UnitTypesKey.Minutes,
          then: (schema) =>
            schema
              .required(commonValidationMessages.requiredValidation)
              .default(""),
          otherwise: (schema) => schema.notRequired(),
        },
      ),

    [CreateDeliverablesFormKey.measurementOther]: Yup.number()
      .nullable()
      .when(
        [
          CreateDeliverablesFormKey.units,
          CreateDeliverablesFormKey.addDeliverableType,
        ],
        {
          is: (unitValue: string, addDeliverableType: AddDeliverableType) =>
            addDeliverableType === ADD_DELIVERABLE_TYPE.newKey &&
            (unitValue === UnitTypesKey.Count ||
              unitValue === UnitTypesKey.Days),
          then: (schema) =>
            schema
              .required(commonValidationMessages.requiredValidation)
              .typeError(commonValidationMessages.requiredValidation)
              .min(1, commonValidationMessages.valueRangeFieldValidator1To99)
              .max(99, commonValidationMessages.valueRangeFieldValidator1To99)
              .default(0),
          otherwise: (schema) => schema.notRequired(),
        },
      ),
  });

export const UpdateDeliverableValidationSchema = (
  commonValidationMessages: KeyValueMap,
) =>
  Yup.object().shape({
    [UpdateDeliverablesFormKey.measurementMinute]: Yup.string()
      .when([UpdateDeliverablesFormKey.units], {
        is: (unitValue: string) => {
          return unitValue === UnitTypesKey.Minutes;
        },
        then: (schema) =>
          schema.required(commonValidationMessages.requiredValidation),
      })
      .default(""),
    [UpdateDeliverablesFormKey.measurementOther]: Yup.number()
      .nullable()
      .when([UpdateDeliverablesFormKey.units], {
        is: (unitValue: string) => {
          return (
            unitValue === UnitTypesKey.Count || unitValue === UnitTypesKey.Days
          );
        },
        then: (schema) =>
          schema
            .required(commonValidationMessages.requiredValidation)
            .typeError(commonValidationMessages.requiredValidation)
            .min(1, commonValidationMessages.valueRangeFieldValidator1To99)
            .max(99, commonValidationMessages.valueRangeFieldValidator1To99),
      })
      .default(0),
  });

export const CreateCompatibleProgramTemplateValidationSchema = (
  commonValidationMessages: KeyValueMap,
) =>
  Yup.object().shape({
    [ProgramTemplateAddCompatibleFormKey.country]: Yup.string().required(
      commonValidationMessages.requiredValidation,
    ),
    [ProgramTemplateAddCompatibleFormKey.compatibleProgramTemplate]:
      Yup.string().required(commonValidationMessages.requiredValidation),
    [ProgramTemplateAddCompatibleFormKey.duration]: Yup.string().optional(),
  });
